import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { DocumentAddIcon } from "@heroicons/react/outline";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import PayrollOpeningBalanceModal from "../../../../Components/v2/Accounting/PayrollOpeningBalanceModal";
import FormSelect from "../../../../Components/v2/Form/FormSelect";
import Table from "../../../../Components/v2/Table";
import Button from "../../../../Components/v2/Button";
import { ModalAlert } from "../../../../Components/ModalAlert";
import {
  addLocalNotification,
  requestListBusinessEmployee,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
  usePayrollOpeningBalances,
} from "../../../../Context";
import { formatMoney } from "../../../../Utils";
import FormError from "../../../../Components/v2/Form/FormError";

const isYear = (value) => /^\d{4}$/.test(value || "");

const employeeName = (employee) =>
  employee ? `${employee.firstName} ${employee.lastName || ""}`.trim() : "";

// Only these employees go through payroll runs, so only they can carry an
// opening balance; the server refuses the rest with the same rule.
const isPayrollEmployee = (employee) =>
  employee.employmentType === "PERMANENT" && employee.salaryType === "MONTHLY";

const AccountingPayrollOpeningBalance = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();
  const { user } = useAuthState();
  const { employees, employeesLoading } = useBusinessTransactionState();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentYear = new Date().getFullYear();
  const yearParam = searchParams.get("year");
  const year = isYear(yearParam) ? Number(yearParam) : currentYear;
  const businessID = user?.mainBusiness?.id;

  const { balances, loading, error, refetch, save, remove } =
    usePayrollOpeningBalances(businessID, year);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Kept after the dialog closes so its text does not blank while it fades.
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    if (businessID) {
      requestListBusinessEmployee(dispatch, { page: 1, limit: 100, businessID });
    }
  }, [dispatch, businessID]);

  const employeesById = useMemo(
    () => Object.fromEntries((employees || []).map((employee) => [employee.id, employee])),
    [employees],
  );

  const monthName = useMemo(() => {
    const format = new Intl.DateTimeFormat(i18n.language === "en" ? "en-US" : "id-ID", {
      month: "long",
    });
    return (month) => format.format(new Date(2000, month - 1, 1));
  }, [i18n.language]);

  // Somebody who already has a balance this year is edited, not added twice.
  const selectableEmployees = useMemo(() => {
    const taken = new Set(balances.map((balance) => balance.employeeID));
    return (employees || []).filter(
      (employee) =>
        isPayrollEmployee(employee) &&
        (editing ? employee.id === editing.employeeID : !taken.has(employee.id)),
    );
  }, [employees, balances, editing]);

  const rows = useMemo(
    () =>
      [...balances].sort((a, b) =>
        employeeName(employeesById[a.employeeID]).localeCompare(
          employeeName(employeesById[b.employeeID]),
          "id",
        ),
      ),
    [balances, employeesById],
  );

  const yearOptions = useMemo(
    () =>
      Array.from({ length: 5 }, (_, index) => {
        const value = currentYear + 1 - index;
        return { value, label: String(value) };
      }),
    [currentYear],
  );

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (balance) => {
    setEditing(balance);
    setModalOpen(true);
  };

  const handleClose = (saved) => {
    setModalOpen(false);
    if (saved) {
      addLocalNotification(notificationDispatch, {
        id: `payroll-opening-balance-${Date.now()}`,
        type: "success",
        title: "common.success",
        body: "payroll.openingBalance.notifications.saved",
        showFull: true,
      });
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await remove(deleting.id);
      setConfirmDelete(false);
      addLocalNotification(notificationDispatch, {
        id: `payroll-opening-balance-delete-${deleting.id}`,
        type: "success",
        title: "common.success",
        body: "payroll.openingBalance.notifications.deleted",
        showFull: true,
      });
    } catch (err) {
      setConfirmDelete(false);
      setDeleteError(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      header: "payroll.openingBalance.table.employee",
      accessor: (balance) => {
        const employee = employeesById[balance.employeeID];
        return (
          <span>
            <span className="block font-medium text-gray-900">
              {employeeName(employee) || balance.employeeID}
            </span>
            {employee?.employeeID && (
              <span className="block text-xs text-gray-500">{employee.employeeID}</span>
            )}
          </span>
        );
      },
    },
    {
      header: "payroll.openingBalance.table.throughMonth",
      accessor: (balance) => monthName(balance.throughMonth),
    },
    {
      header: "payroll.openingBalance.table.ptkp",
      accessor: (balance) => `${balance.marriageStatus}/${balance.dependent}`,
    },
    {
      header: "payroll.openingBalance.table.gross",
      align: "right",
      className: "tabular-nums text-gray-900",
      accessor: (balance) => formatMoney(balance.grossIncome),
    },
    {
      header: "payroll.openingBalance.table.taxWithheld",
      align: "right",
      className: "tabular-nums text-gray-900",
      accessor: (balance) => formatMoney(balance.taxWithheld),
    },
    {
      header: "common.actions.label",
      align: "right",
      accessor: (balance) => {
        const name = employeeName(employeesById[balance.employeeID]);
        return (
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => openEdit(balance)}
              aria-label={t("payroll.openingBalance.actions.editFor", { name })}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {t("common.edit")}
            </button>
            <button
              type="button"
              onClick={() => {
                setDeleteError(null);
                setDeleting(balance);
                setConfirmDelete(true);
              }}
              aria-label={t("payroll.openingBalance.actions.deleteFor", { name })}
              className="text-sm font-medium text-red-600 hover:text-red-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              {t("common.delete")}
            </button>
          </div>
        );
      },
    },
  ];

  const deletingName = deleting
    ? employeeName(employeesById[deleting.employeeID]) || deleting.employeeID
    : "";

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="payroll.openingBalance.title"
          additionalActions={
            rows.length > 0 ? (
              <Button onClick={openAdd} leadingIcon={DocumentAddIcon}>
                {t("payroll.openingBalance.actions.add")}
              </Button>
            ) : null
          }
        />

        <div className="px-4 py-5 sm:px-6 lg:px-8 space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <p className="text-sm text-gray-600 max-w-2xl">
              {t("payroll.openingBalance.intro")}
            </p>
            <div className="w-full sm:w-40 flex-shrink-0">
              <FormSelect
                label="payroll.openingBalance.filter.year"
                options={yearOptions}
                value={yearOptions.find((option) => option.value === year) || {
                  value: year,
                  label: String(year),
                }}
                onChange={(option) => option && setSearchParams({ year: String(option.value) })}
              />
            </div>
          </div>

          <FormError error={deleteError} />

          {error || loading || rows.length > 0 ? (
            <Table
              columns={columns}
              data={rows}
              isLoading={loading || (employeesLoading && rows.length === 0)}
              error={error}
              onRetry={refetch}
            />
          ) : (
            <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white px-6 py-10 text-center">
              <DocumentAddIcon className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
              <h3 className="mt-3 text-sm font-semibold text-gray-900">
                {t("payroll.openingBalance.empty.title", { year })}
              </h3>
              <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                {t("payroll.openingBalance.empty.body")}
              </p>
              <Button className="mt-5" onClick={openAdd}>
                {t("payroll.openingBalance.actions.add")}
              </Button>
            </div>
          )}
        </div>

        <PayrollOpeningBalanceModal
          open={modalOpen}
          onClose={handleClose}
          year={year}
          balance={editing}
          employees={selectableEmployees}
          onSave={save}
        />

        <ModalAlert
          open={confirmDelete}
          setOpen={setConfirmDelete}
          title="payroll.openingBalance.delete.title"
          descriptionNode={t("payroll.openingBalance.delete.body", {
            name: deletingName,
            year,
          })}
          confirmButtonText="common.delete"
          cancelButtonText="common.cancel"
          loadingButton={deleteLoading}
          disabledButton={deleteLoading}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      </main>
    </AccountingWrapper>
  );
};

export default AccountingPayrollOpeningBalance;
